/**
 * WhatsAppPopup – erscheint bei jedem WhatsApp-Klick auf der Website
 * Design: Immozwerg-Brandfarben (#631172 / #a066c2 / #e6dcd2)
 * Felder: Name, E-Mail, Anfragegrund (Auswahl)
 * Nach Absenden: WhatsApp mit vorausgefüllter Nachricht öffnen
 */
import { useState, useCallback } from "react";
import { X, MessageCircle, ChevronDown } from "lucide-react";

const WHATSAPP_NUMBER = "491738660756";

const ANFRAGEGRÜNDE = [
  "Immobilie verkaufen",
  "Immobilie kaufen / suchen",
  "Wertermittlung",
  "Erbschaft / Scheidung",
  "Finanzierung",
  "Sonstiges",
];

interface WhatsAppPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WhatsAppPopup({ isOpen, onClose }: WhatsAppPopupProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [grund, setGrund] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!name.trim() || !grund) return;

      // WhatsApp-Nachricht zusammenbauen
      const msg = `Hallo Andrea! 👋\n\nMein Name ist *${name.trim()}*${email.trim() ? ` (${email.trim()})` : ""}.\n\nIch melde mich wegen: *${grund}*\n\nBitte melde dich bei mir. Vielen Dank! 😊`;
      const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

      setSubmitted(true);

      // Kurz warten, dann WhatsApp öffnen und Popup schließen
      setTimeout(() => {
        window.open(waUrl, "_blank", "noopener,noreferrer");
        // Reset nach Öffnen
        setTimeout(() => {
          setSubmitted(false);
          setName("");
          setEmail("");
          setGrund("");
          onClose();
        }, 800);
      }, 600);
    },
    [name, email, grund, onClose]
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="wa-popup-title"
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="px-6 pt-6 pb-5 text-white"
            style={{ background: "linear-gradient(135deg, #631172 0%, #a066c2 100%)" }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              aria-label="Schließen"
            >
              <X size={16} className="text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <MessageCircle size={22} className="text-white" />
              </div>
              <div>
                <h2 id="wa-popup-title" className="font-bold text-lg leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Kurz vorstellen?
                </h2>
                <p className="text-white/80 text-sm">Damit ich direkt weiß, worum es geht.</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="wa-name" className="block text-xs font-bold uppercase tracking-wider text-[#a066c2] mb-1.5">
                Dein Name *
              </label>
              <input
                id="wa-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Vorname Nachname"
                className="w-full px-4 py-2.5 rounded-xl border border-[#e8dff0] bg-[#faf8f5] text-[#1a1228] text-sm focus:outline-none focus:ring-2 focus:ring-[#a066c2] focus:border-transparent transition-all placeholder:text-gray-400"
              />
            </div>

            {/* E-Mail */}
            <div>
              <label htmlFor="wa-email" className="block text-xs font-bold uppercase tracking-wider text-[#a066c2] mb-1.5">
                E-Mail <span className="text-gray-400 font-normal normal-case">(optional)</span>
              </label>
              <input
                id="wa-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="deine@email.de"
                className="w-full px-4 py-2.5 rounded-xl border border-[#e8dff0] bg-[#faf8f5] text-[#1a1228] text-sm focus:outline-none focus:ring-2 focus:ring-[#a066c2] focus:border-transparent transition-all placeholder:text-gray-400"
              />
            </div>

            {/* Anfragegrund */}
            <div>
              <label htmlFor="wa-grund" className="block text-xs font-bold uppercase tracking-wider text-[#a066c2] mb-1.5">
                Worum geht es? *
              </label>
              <div className="relative">
                <select
                  id="wa-grund"
                  required
                  value={grund}
                  onChange={(e) => setGrund(e.target.value)}
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-[#e8dff0] bg-[#faf8f5] text-[#1a1228] text-sm focus:outline-none focus:ring-2 focus:ring-[#a066c2] focus:border-transparent transition-all appearance-none cursor-pointer"
                >
                  <option value="" disabled>Bitte auswählen …</option>
                  {ANFRAGEGRÜNDE.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a066c2] pointer-events-none" />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitted}
              className="w-full py-3 rounded-xl font-bold text-white text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: submitted ? "#a066c2" : "linear-gradient(135deg, #25D366, #128C7E)" }}
            >
              {submitted ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  WhatsApp wird geöffnet …
                </>
              ) : (
                <>
                  <MessageCircle size={16} />
                  Weiter zu WhatsApp
                </>
              )}
            </button>

            <p className="text-center text-[11px] text-gray-400">
              Deine Daten werden nur zur Kontaktaufnahme verwendet.
            </p>
          </form>
        </div>
      </div>
    </>
  );
}

/**
 * useWhatsAppPopup – Hook zum Steuern des Popups
 * Verwendung: const { openWhatsApp, popupElement } = useWhatsAppPopup();
 * Dann: <button onClick={openWhatsApp}>WhatsApp</button>
 *       {popupElement}
 */
export function useWhatsAppPopup() {
  const [isOpen, setIsOpen] = useState(false);

  const openWhatsApp = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsOpen(true);
  }, []);

  const closePopup = useCallback(() => setIsOpen(false), []);

  const popupElement = (
    <WhatsAppPopup isOpen={isOpen} onClose={closePopup} />
  );

  return { openWhatsApp, popupElement };
}
